import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useTenantDeletions } from "@/hooks/useTenants";

interface RequestDeletionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: string | null;
  tenantName?: string;
}

export function RequestDeletionDialog({ 
  open, 
  onOpenChange, 
  tenantId, 
  tenantName 
}: RequestDeletionDialogProps) {
  const [reason, setReason] = useState("");
  const { requestDeletion, isRequesting } = useTenantDeletions();

  const handleSubmit = async () => {
    if (!tenantId) return;
    
    await requestDeletion({ customerId: tenantId, reason });
    setReason("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <div className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            <DialogTitle>Request Tenant Deletion</DialogTitle>
          </div>
          <DialogDescription>
            You are requesting the deletion of <strong>{tenantName}</strong>. This request will be sent to Smplify for approval.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="p-4 bg-destructive/10 rounded-lg border border-destructive/20">
            <p className="text-sm text-destructive">
              <strong>Warning:</strong> Once approved, all data associated with this tenant will be permanently deleted, including:
            </p>
            <ul className="text-sm text-destructive mt-2 list-disc list-inside">
              <li>All enrolled devices</li>
              <li>Device configurations and profiles</li>
              <li>User accounts and permissions</li>
              <li>Historical data and logs</li>
            </ul>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Reason for deletion</Label>
            <Textarea
              id="reason"
              placeholder="Please provide a reason for this deletion request..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            variant="destructive" 
            onClick={handleSubmit}
            disabled={isRequesting}
          >
            {isRequesting ? "Submitting..." : "Submit Request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
